import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { classificationService } from '../../services/classificationService';
import CategoryForm from '../../components/Classification/CategoryForm';
import ClassificationExcelToolbar from '../../components/Classification/ClassificationExcelToolbar';
import {
  ClassificationBulkDeleteModal,
  ClassificationBulkDeleteTd,
  ClassificationBulkDeleteTh,
  ClassificationBulkDeleteToolbar,
} from '../../components/Classification/ClassificationBulkDeleteUi';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { useClassificationBulkDelete } from '../../hooks/useClassificationBulkDelete';
import { useClassificationHierarchyForBulk } from '../../hooks/useClassificationHierarchyForBulk';

const Categories = () => {
  const canMutate = useCanMutate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const { hierarchy: fullHierarchy, reloadHierarchy } = useClassificationHierarchyForBulk(
    classificationService,
    categories.length
  );

  useEffect(() => {
    // Evitar doble carga en React StrictMode
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadCategories();
    }
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      console.log('📁 [Categories] Cargando categorías...');
      console.log('🌐 [Categories] Enviando petición GET a:', '/api/classification/categories');
      
      // Verificar que hay token antes de hacer la petición
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ [Categories] No hay token disponible, redirigiendo a login');
        window.location.href = '/login';
        return;
      }
      
      const response = await classificationService.getCategories();
      
      console.log('✅ [Categories] Categorías cargadas exitosamente');
      console.log('📊 [Categories] Respuesta:', response);
      console.log('📦 [Categories] Total de categorías:', response.data?.length || 0);
      
      if (response.success) {
        setCategories(response.data);
        console.log('💾 [Categories] Categorías guardadas en estado');
      } else {
        console.warn('⚠️ [Categories] Respuesta sin éxito:', response);
      }
    } catch (error) {
      console.error('❌ [Categories] Error al cargar categorías:', error);
      console.error('📊 [Categories] Respuesta del servidor:', error.response?.data);
      console.error('🔢 [Categories] Status:', error.response?.status);
      console.error('📋 [Categories] Mensaje:', error.message);
      
      // Si es un error 403 o de acceso denegado, el interceptor ya debería haber redirigido
      // Pero por si acaso, verificamos aquí también
      if (error.response?.status === 403 || error.message?.includes('403') || error.message?.includes('denegado')) {
        console.error('❌ [Categories] Error 403 detectado en catch, limpiando sesión');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // No redirigir aquí porque el interceptor ya debería haberlo hecho
      }
    } finally {
      setLoading(false);
      console.log('🏁 [Categories] Proceso de carga finalizado');
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      console.log('🗑️ [Categories] Eliminando categoría:', categoryToDelete.id);
      const response = await classificationService.deleteCategory(categoryToDelete.id);
      
      if (response.success) {
        console.log('✅ [Categories] Categoría eliminada exitosamente');
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
        setToast({
          isOpen: true,
          message: 'Categoría eliminada exitosamente',
          type: 'success'
        });
        loadCategories(); // Recargar la lista
      } else {
        console.error('❌ [Categories] Error al eliminar:', response.message);
        setIsDeleteModalOpen(false);
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la categoría',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Categories] Error al eliminar:', error);
      setIsDeleteModalOpen(false);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la categoría',
        type: 'error'
      });
    }
  };

  const handleFormSuccess = () => {
    console.log('✅ [Categories] Formulario guardado exitosamente, recargando lista...');
    loadCategories();
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const bulk = useClassificationBulkDelete({
    variant: 'categories',
    rows: filteredCategories,
    hierarchy: fullHierarchy,
    blockRecetaOrigin: true,
    deleteItem: (id) => classificationService.deleteCategory(id),
    onDeleted: async () => {
      await loadCategories();
      await reloadHierarchy();
    },
    setToast,
    enabled: canMutate,
  });

  const isFromRecetas = (row) => row?.receta_category_id != null && row.receta_category_id !== '';

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Cargando categorías...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Categorías</h1>
              <p className="text-gray-600 mt-1">Nivel 1 - Clasificación de Productos</p>
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <MutateGate>
                <ClassificationExcelToolbar
                  variant="categories"
                  onImported={loadCategories}
                  setToast={setToast}
                  exportRows={filteredCategories}
                />
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategory(null);
                    setIsFormOpen(true);
                  }}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
                >
                  + Nueva Categoría
                </button>
              </MutateGate>
            </div>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/classification" className="hover:text-teal-600">Clasificación</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Categorías</span>
          </nav>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Buscar categorías..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total Categorías</div>
            <div className="text-2xl font-bold text-gray-800">{categories.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Categorías Activas</div>
            <div className="text-2xl font-bold text-green-600">
              {categories.filter(c => c.is_active).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Categorías Inactivas</div>
            <div className="text-2xl font-bold text-red-600">
              {categories.filter(c => !c.is_active).length}
            </div>
          </div>
        </div>

        {/* Table */}
        <ClassificationBulkDeleteToolbar bulk={bulk} />

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredCategories.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm ? 'No se encontraron categorías con ese criterio' : 'No hay categorías registradas'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <ClassificationBulkDeleteTh bulk={bulk} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCategories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <ClassificationBulkDeleteTd bulk={bulk} row={category} />
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{category.code}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 inline-flex items-center gap-2 flex-wrap">
                        {category.name}
                        {isFromRecetas(category) ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200" title="Sincronizada desde recetas; no se puede eliminar aquí">
                            Recetas
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-500">{category.description || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        category.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {category.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/classification/subcategories?category=${category.id}`}
                        className="text-teal-600 hover:text-teal-900 mr-4"
                      >
                        Ver Subcategorías
                      </Link>
                      {canMutate ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCategory(category);
                              setIsFormOpen(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 mr-4"
                          >
                            Editar
                          </button>
                          {isFromRecetas(category) ? (
                            <span className="text-xs text-gray-500 max-w-[10rem] inline-block align-top" title="Elimine o gestione esta categoría desde Producción → Clasificación de recetas">
                              Sin eliminar (recetas)
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(category)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Eliminar
                            </button>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal de Formulario */}
      <CategoryForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSuccess={handleFormSuccess}
      />

      <ClassificationBulkDeleteModal bulk={bulk} />

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Categoría"
        message={`¿Estás seguro de que deseas eliminar la categoría "${categoryToDelete?.name}" (${categoryToDelete?.code})? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Categories;
